import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DirectionsRailwayIcon from "@mui/icons-material/DirectionsRailway";
import YardIcon from "@mui/icons-material/Yard";
import BuildIcon from "@mui/icons-material/Build";
import EngineeringIcon from "@mui/icons-material/Engineering";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import api from "../../services/api";


//const API_URL = "/api/dashboard/desktop";
const cardSx = {
  height: "100%",
  borderRadius: { xs: 2.5, sm: 3 },
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 1px 2px rgba(15,23,42,.04)",
  overflow: "hidden",
};
const contentSx = {
  p: { xs: 1.5, sm: 2, md: 2.25 },
  "&:last-child": { pb: { xs: 1.5, sm: 2, md: 2.25 } },
};
const first = (...values) =>
  values.find((v) => v !== undefined && v !== null && v !== "");
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const dateText = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};
const timeText = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};
const statusColor = (s) =>
  ({
    WORKING: "success",
    UNDER_REPAIR: "success",
    ACTIVE: "success",
    WAITING: "warning",
    YARD: "warning",
    PARK_IN_YARD: "warning",
    INSPECTION: "info",
    UNDER_INSPECTION: "info",
    READY: "primary",
    READY_OUTTURN: "primary",
    CANCELLED: "error",
  })[String(s || "").toUpperCase()] || "default";
const arr = (...v) => {
  const x = first(...v);
  return Array.isArray(x) ? x : [];
};

function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
      spacing={1.2}
      sx={{ mb: 1.7 }}
    >
      <Stack
        direction="row"
        spacing={1.1}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            bgcolor: "primary.50",
            color: "primary.main",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            fontWeight={750}
            sx={{ fontSize: { xs: ".98rem", sm: "1.05rem" }, lineHeight: 1.2 }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>
      {action && (
        <Box sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
          {action}
        </Box>
      )}
    </Stack>
  );
}
function Empty({ children = "No records found." }) {
  return (
    <Box sx={{ py: 3, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
}
function Kpi({ item }) {
  const Icon = item.icon;
  return (
    <Card
      elevation={0}
      sx={{
        ...cardSx,
        minHeight: { xs: 122, sm: 138, lg: 145 },
        position: "relative",
        "&:before": {
          content: '""',
          position: "absolute",
          inset: "0 auto 0 0",
          width: 4,
          bgcolor: `${item.color}.main`,
        },
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-2px)" },
          boxShadow: { xs: "none", sm: 3 },
        },
      }}
    >
      <CardContent sx={{ ...contentSx, height: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ height: "100%" }}
        >
          <Box sx={{ minWidth: 0, pr: 0.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
            >
              {item.title}
            </Typography>
            <Typography
              sx={{
                mt: 0.65,
                fontSize: { xs: "1.65rem", sm: "1.9rem", lg: "2rem" },
                lineHeight: 1,
                fontWeight: 800,
              }}
            >
              {num(item.value).toLocaleString("en-IN")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.7 }}
            >
              {item.subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              bgcolor: `${item.color}.50`,
              color: `${item.color}.main`,
            }}
          >
            <Icon />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
function CoachMobile({ c }) {
  return (
    <Box
      sx={{
        p: 1.3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={800} noWrap>
            Coach {first(c.coachNo, c.coach_no, "—")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Visit {first(c.visitNo, c.visit_no, "—")}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={first(c.status, "—")}
          color={statusColor(c.status)}
          variant="outlined"
        />
      </Stack>
      <Divider sx={{ my: 1 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <LocationOnIcon fontSize="small" color="action" />
          <Typography variant="body2" noWrap>
            {first(
              c.location,
              c.current_location,
              c.current_location_code,
              "—",
            )}
          </Typography>
        </Stack>
        <Chip
          size="small"
          variant="outlined"
          icon={<AccessTimeIcon sx={{ fontSize: "15px !important" }} />}
          label={`${num(first(c.days, c.days_in_workshop, 0))} days`}
        />
      </Stack>
    </Box>
  );
}
function CoachTable({ rows, mobile, total }) {
  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={contentSx}>
        <SectionHeader
          icon={<DirectionsRailwayIcon />}
          title="Current Coaches"
          subtitle="Coaches presently under workshop custody"
          action={
            <Chip
              label={`${num(total)} Active`}
              color="primary"
              size="small"
              variant="outlined"
            />
          }
        />
        {!rows.length ? (
          <Empty>No active coaches are currently listed.</Empty>
        ) : mobile ? (
          <Stack spacing={1}>
            {rows.map((c, i) => (
              <CoachMobile
                key={first(c.visit_id, c.visitNo, c.visit_no, i)}
                c={c}
              />
            ))}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "action.hover",
                    "& th": { fontWeight: 700, whiteSpace: "nowrap" },
                  }}
                >
                  <TableCell>Coach No.</TableCell>
                  <TableCell>Visit No.</TableCell>
                  <TableCell>Current Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Days</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((c, i) => (
                  <TableRow
                    hover
                    key={first(c.visit_id, c.visitNo, c.visit_no, i)}
                  >
                    <TableCell>
                      <b>{first(c.coachNo, c.coach_no, "—")}</b>
                    </TableCell>
                    <TableCell>{first(c.visitNo, c.visit_no, "—")}</TableCell>
                    <TableCell>
                      {first(
                        c.location,
                        c.current_location,
                        c.current_location_code,
                        "—",
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={first(c.status, "—")}
                        color={statusColor(c.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {num(first(c.days, c.days_in_workshop, 0))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
function ShopWorkload({ rows, total }) {
  const max = Math.max(
    1,
    ...rows.map((s) => num(first(s.count, s.coach_count, s.total))),
  );
  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={contentSx}>
        <SectionHeader
          icon={<BuildIcon />}
          title="Shop Workload"
          subtitle="Current active work"
          action={
            <Chip
              label={`${num(total)} Coaches`}
              color="success"
              size="small"
              variant="outlined"
            />
          }
        />
        {!rows.length ? (
          <Empty>No shop workload data available.</Empty>
        ) : (
          <Stack spacing={1.1}>
            {rows.map((s, i) => {
              const count = num(first(s.count, s.coach_count, s.total));
              const pct = Math.round((count / max) * 100);
              return (
                <Box
                  key={first(s.name, s.shop_name, s.shop_code, i)}
                  sx={{
                    p: 1.2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2.5,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 33,
                        height: 33,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "primary.50",
                        color: "primary.main",
                      }}
                    >
                      <BuildIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {first(s.name, s.shop_name, s.shop_code, "Unassigned")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active workload
                      </Typography>
                    </Box>
                    <Chip label={count} size="small" color="primary" />
                    <Typography variant="caption" fontWeight={700}>
                      {pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ mt: 1, height: 6, borderRadius: 10 }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
function MovementMobile({ m }) {
  return (
    <Box
      sx={{
        p: 1.3,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography fontWeight={800}>
          Coach {first(m.coachNo, m.coach_no, "—")}
        </Typography>
        <Chip
          size="small"
          label={first(m.status, "—")}
          color={statusColor(m.status)}
          variant="outlined"
        />
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
        <Chip
          size="small"
          variant="outlined"
          label={first(m.from, m.from_location, "—")}
        />
        <ArrowForwardIcon fontSize="small" color="action" />
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={first(m.to, m.to_location, "—")}
        />
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {first(m.time, timeText(m.movement_datetime), "—")}
        </Typography>
      </Stack>
    </Box>
  );
}
function Movements({ rows, mobile }) {
  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={contentSx}>
        <SectionHeader
          icon={<LocationOnIcon />}
          title="Recent Coach Movements"
          subtitle="Latest movements inside workshop"
        />
        {!rows.length ? (
          <Empty>No recent movements found.</Empty>
        ) : mobile ? (
          <Stack spacing={1}>
            {rows.map((m, i) => (
              <MovementMobile key={first(m.id, `${m.coach_no}-${i}`)} m={m} />
            ))}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: "action.hover", "& th": { fontWeight: 700 } }}
                >
                  <TableCell>Coach</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((m, i) => (
                  <TableRow hover key={first(m.id, `${m.coach_no}-${i}`)}>
                    <TableCell>
                      <b>{first(m.coachNo, m.coach_no, "—")}</b>
                    </TableCell>
                    <TableCell>{first(m.from, m.from_location, "—")}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <ArrowForwardIcon fontSize="small" color="action" />
                        {first(m.to, m.to_location, "—")}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {first(m.time, timeText(m.movement_datetime), "—")}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={first(m.status, "—")}
                        color={statusColor(m.status)}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
function Activity({ rows }) {
  return (
    <Card elevation={0} sx={cardSx}>
      <CardContent sx={contentSx}>
        <SectionHeader
          icon={<ScheduleIcon />}
          title="Recent Activity"
          subtitle="Latest workshop activity"
        />
        {!rows.length ? (
          <Empty>No recent activity found.</Empty>
        ) : (
          <Stack>
            {rows.map((a, i) => (
              <Box
                key={first(a.id, i)}
                sx={{
                  py: 1.2,
                  borderBottom: i !== rows.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1.2}>
                  <Box
                    sx={{
                      mt: 0.65,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: i === 0 ? "primary.main" : "action.disabled",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={650}>
                      {first(
                        a.title,
                        a.description,
                        a.activity,
                        "Workshop activity",
                      )}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                      sx={{ mt: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {first(
                          a.time,
                          timeText(a.activity_datetime || a.created_at),
                          "—",
                        )}
                      </Typography>
                      <Chip
                        label={first(a.type, a.activity_type, "Activity")}
                        size="small"
                        variant="outlined"
                        sx={{ height: 21, fontSize: 10 }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
function DailySummary({ data }) {
  const items = [
    [
      "Gate In Today",
      first(data.gateInToday, data.gate_in_today, data.gate_in, 0),
      "primary",
    ],
    [
      "Shop Movements",
      first(data.shopMovements, data.shop_movements, data.movements, 0),
      "info",
    ],
    [
      "Inspections",
      first(data.inspections, data.inspection_today, 0),
      "warning",
    ],
    [
      "Gate Out Today",
      first(data.gateOutToday, data.gate_out_today, data.gate_out, 0),
      "success",
    ],
  ];
  return (
    <Card elevation={0} sx={{ ...cardSx, mt: 2 }}>
      <CardContent sx={contentSx}>
        <SectionHeader
          icon={<TrendingUpIcon />}
          title="Today's Operational Summary"
          subtitle="Workshop activity overview"
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2,minmax(0,1fr))",
              sm: "repeat(4,minmax(0,1fr))",
            },
            gap: 1.3,
          }}
        >
          {items.map(([label, value, color]) => (
            <Box
              key={label}
              sx={{
                p: 1.4,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2.5,
                position: "relative",
                "&:before": {
                  content: '""',
                  position: "absolute",
                  inset: "0 auto 0 0",
                  width: 3,
                  bgcolor: `${color}.main`,
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                {label}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                {num(value).toLocaleString("en-IN")}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}
function Upcoming({ rows }) {
  return (
    <Card elevation={0} sx={{ ...cardSx, mt: 2 }}>
      <CardContent sx={contentSx}>
        <SectionHeader
          icon={<EventAvailableIcon />}
          title="Upcoming Return Dates"
          subtitle="Scheduled next return dates"
        />
        {!rows.length ? (
          <Empty>No upcoming return dates available.</Empty>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{ bgcolor: "action.hover", "& th": { fontWeight: 700 } }}
                >
                  <TableCell>Coach No.</TableCell>
                  <TableCell>Visit No.</TableCell>
                  <TableCell>Next Return Date</TableCell>
                  <TableCell>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={first(r.visit_id, r.id, i)}>
                    <TableCell>{first(r.coach_no, r.coachNo, "—")}</TableCell>
                    <TableCell>{first(r.visit_no, r.visitNo, "—")}</TableCell>
                    <TableCell>
                      {dateText(first(r.next_return_date, r.nextReturnDate))}
                    </TableCell>
                    <TableCell>
                      {first(r.location, r.current_location, "—")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}
function unwrap(response) {
  const root = response?.data ?? response ?? {};
  return root.dashboard ?? root.result ?? root;
}

export default function WorkshopDashboard() {
  const theme = useTheme(),
    mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState({}),
    [loading, setLoading] = useState(true),
    [refreshing, setRefreshing] = useState(false),
    [error, setError] = useState("");
  const load = useCallback(async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/dashboard/desktop", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(unwrap(res.data));



      console.log("Dashboard data loaded:", unwrap(res.data));

      
    } catch (e) {
      setError(
        e.response?.data?.message ||
          e.response?.data?.error ||
          e.message ||
          "Unable to load dashboard.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const summary = data.summary ?? data.kpis ?? data.stats ?? {};
  const coaches = useMemo(
    () =>
      arr(
        data.currentCoaches,
        data.current_coaches,
        data.coaches,
        data.activeCoaches,
      ),
    [data],
  );
  const shops = useMemo(
    () => arr(data.shopWorkload, data.shop_workload, data.shops),
    [data],
  );
  const movements = useMemo(
    () => arr(data.recentMovements, data.recent_movements, data.movements),
    [data],
  );
  const activities = useMemo(
    () => arr(data.recentActivity, data.recent_activity, data.activities),
    [data],
  );
  const upcoming = useMemo(
    () =>
      arr(
        data.upcomingReturns,
        data.upcoming_returns,
        data.upcomingVisits,
        data.upcoming_visits,
      ),
    [data],
  );
  const kpis = [
    [
      "Coaches in Workshop",
      first(
        summary.inWorkshop,
        summary.coaches_in_workshop,
        summary.totalInWorkshop,
        summary.total_in_workshop,
        summary.total,
        0,
      ),
      "Currently in custody",
      DirectionsRailwayIcon,
      "primary",
    ],
    [
      "In Yard",
      first(summary.inYard, summary.in_yard, summary.yard, 0),
      "Awaiting shop allocation",
      YardIcon,
      "warning",
    ],
    [
      "Under Repair",
      first(summary.underRepair, summary.under_repair, summary.repair, 0),
      "Work in progress",
      BuildIcon,
      "success",
    ],
    [
      "Under POH",
      first(summary.underPoh, summary.under_poh, summary.poh, 0),
      "Active POH jobs",
      EngineeringIcon,
      "info",
    ],
    [
      "Inspection",
      first(
        summary.inspection,
        summary.underInspection,
        summary.under_inspection,
        0,
      ),
      "Awaiting inspection",
      FactCheckIcon,
      "secondary",
    ],
    [
      "Ready Outturn",
      first(summary.completed, summary.ready_outturn, summary.ready, 0),
      "Ready for gate out",
      ExitToAppIcon,
      "error",
    ],
  ].map(([title, value, subtitle, icon, color]) => ({
    title,
    value,
    subtitle,
    icon,
    color,
  }));
  const activeTotal = first(
    summary.inWorkshop,
    summary.coaches_in_workshop,
    summary.totalInWorkshop,
    summary.total_in_workshop,
    coaches.length,
    0,
  );
  const shopTotal = shops.reduce(
    (s, x) => s + num(first(x.count, x.coach_count, x.total)),
    0,
  );
  const date = first(
    data.date,
    data.currentDate,
    data.current_date,
    new Date(),
  );
  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: "100vh",
        bgcolor: "background.default",
        p: { xs: 1, sm: 2, md: 2.5, lg: 3 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 1.5,
          mb: 2.3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "grid",
              placeItems: "center",
            }}
          >
            <DirectionsRailwayIcon />
          </Box>
          <Box>
            <Typography
              variant={mobile ? "h5" : "h4"}
              fontWeight={800}
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.65rem", md: "2rem" },
                lineHeight: 1.1,
              }}
            >
              Workshop Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Railway coach repair &amp; POH operations
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={0.8}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            icon={<EventAvailableIcon />}
            label={dateText(date)}
            variant="outlined"
          />
          <Chip label="LIVE DATA" color="success" variant="outlined" />
          <Button
            size="small"
            variant="outlined"
            startIcon={
              refreshing ? <CircularProgress size={15} /> : <RefreshIcon />
            }
            onClick={() => load(true)}
            disabled={loading || refreshing}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => load(true)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ minHeight: 240, display: "grid", placeItems: "center" }}>
          <Stack alignItems="center" spacing={1.5}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading workshop dashboard…
            </Typography>
          </Stack>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2,minmax(0,1fr))",
                sm: "repeat(3,minmax(0,1fr))",
                lg: "repeat(6,minmax(0,1fr))",
              },
              gap: { xs: 1.1, sm: 1.5, lg: 1.75 },
            }}
          >
            {kpis.map((item) => (
              <Box key={item.title} sx={{ minWidth: 0, display: "flex" }}>
                <Kpi item={item} />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0,1fr)",
                lg: "minmax(0,2fr) minmax(300px,1fr)",
              },
              gap: { xs: 1.5, sm: 2 },
              mt: 2,
              alignItems: "stretch",
            }}
          >
            <CoachTable rows={coaches} mobile={mobile} total={activeTotal} />
            <ShopWorkload rows={shops} total={shopTotal} />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0,1fr)",
                lg: "minmax(0,2fr) minmax(300px,1fr)",
              },
              gap: { xs: 1.5, sm: 2 },
              mt: 2,
              alignItems: "stretch",
            }}
          >
            <Movements rows={movements} mobile={mobile} />
            <Activity rows={activities} />
          </Box>
          <DailySummary
            data={data.dailySummary ?? data.daily_summary ?? summary}
          />
          <Upcoming rows={upcoming} />
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              justifyContent: { xs: "center", sm: "space-between" },
              flexWrap: "wrap",
              gap: 0.5,
              px: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Workshop Management System • Operational Dashboard
            </Typography>
            {first(
              data.lastUpdated,
              data.last_updated,
              data.generatedAt,
              data.generated_at,
            ) && (
              <Typography variant="caption" color="text.secondary">
                Updated:{" "}
                {dateText(
                  first(
                    data.lastUpdated,
                    data.last_updated,
                    data.generatedAt,
                    data.generated_at,
                  ),
                )}{" "}
                {timeText(
                  first(
                    data.lastUpdated,
                    data.last_updated,
                    data.generatedAt,
                    data.generated_at,
                  ),
                )}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
